/**
 * The two shapes a Play button takes in this product, and both of them are this file now.
 *
 * WHAT MADE IT SHARED. The transport - a play button, a scrubber and a clock - was written
 * for the opening line's card, where the recording is six seconds long. A call recording is
 * five minutes long and wants exactly the same control, so the choice was a second copy of
 * ninety lines of pointer capture and slider ARIA, or this. The 32px square has the same
 * story from the other direction: the voice picker drew forty of them, and the calls list
 * now draws fifty.
 *
 * NEITHER OF THEM IS `<audio controls>`. The browser's player is a 300px slab of somebody
 * else's design language - Chrome's is dark grey with its own radius, its own blue and a
 * volume slider - and it only reaches full size once it has bytes, so the row it sits in
 * changes shape when you press play. There is no `<audio>` element in the tree at all;
 * `useClip` holds a plain `new Audio()` in a ref, which is the playback engine and never a
 * control.
 *
 * THE BYTES ARE FETCHED RATHER THAN POINTED AT WITH `src`. Every one of these routes needs
 * the session cookie and answers 404 for something that is not this reader's, and a bare
 * `src` gives neither `credentials: "include"` on a split-origin dev setup nor any way to
 * show what went wrong - a failed load would be a control that silently does nothing. So the
 * failure is a sentence, and the object URL is revoked on unmount.
 *
 * EVERY PATH THAT REACHES HERE CARRIES `?v=<sha256>`, which is what makes the Worker's
 * `immutable` honest: the URL names one file, and there is no version of it that goes stale.
 */

import { useEffect, useRef } from "react";
import { Pause, Play } from "./Icons";
import { useClip } from "./useClip";
import { cx } from "../ui";

/**
 * A full transport: play, a scrubber you can drag, and a clock.
 *
 * `durationMs` is what the caller already knows from the row it is drawing, so the clock and
 * the track are honest on first paint rather than counting up from an unknown total. The
 * file's own duration wins if the two ever disagree.
 */
export function RecordingPlayer({
  path,
  durationMs,
  className,
}: {
  path: string;
  durationMs: number;
  className?: string;
}) {
  const clip = useClip(path, durationMs);

  function scrub(event: React.PointerEvent<HTMLDivElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width === 0) return;
    clip.seek((event.clientX - box.left) / box.width);
  }

  const played = clip.total > 0 ? Math.min(1, clip.at / clip.total) : 0;
  // A SECOND AT A TIME ON A SIX-SECOND GREETING AND FIVE ON A FIVE-MINUTE CALL. A fixed step
  // makes one of the two useless: five seconds skips most of a greeting, and one second
  // means eighty presses to cross a call. A twentieth of the whole, floored at a second.
  const step = Math.max(1, Math.round(clip.total / 60));

  return (
    <div className={cx("flex min-w-[240px] flex-1 items-center gap-2.5", className)}>
      <button
        type="button"
        onClick={clip.toggle}
        disabled={clip.loading}
        aria-label={clip.playing ? "Pause" : "Play"}
        title={clip.playing ? "Pause" : "Play"}
        className={cx(CONTROL, "rounded-full text-ink")}
      >
        {clip.playing ? <Pause size={15} /> : <Play size={15} fill="currentColor" />}
      </button>

      {/*
        A slider, said in the terms a screen reader already has for one. The 24px box is
        hit area - a 4px track is a target nobody can hit - and the arrow keys move in a
        step scaled to the clip, because a percentage step is meaningless on six seconds.
      */}
      <div
        role="slider"
        tabIndex={0}
        aria-label="Position in the recording"
        aria-valuemin={0}
        aria-valuemax={Math.round(clip.total)}
        aria-valuenow={Math.round(clip.at)}
        aria-valuetext={`${clock(clip.at)} of ${clock(clip.total)}`}
        className="group relative h-6 min-w-[60px] flex-1 cursor-pointer touch-none"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          scrub(event);
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) scrub(event);
        }}
        onKeyDown={(event) => {
          const move = event.key === "ArrowRight" ? step : event.key === "ArrowLeft" ? -step : 0;
          if (!move || clip.total === 0) return;
          event.preventDefault();
          clip.seek((clip.at + move) / clip.total);
        }}
      >
        <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-line" />
        <div
          className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-accent"
          style={{ width: `${played * 100}%` }}
        />
        {/* THE HANDLE COMES AND GOES. Parked at 0:00 it is a stray dot on a grey rule -
            there is no progress for it to be the end of - so it fades in for the pointer,
            for the keyboard, and for the whole time the thing is playing. */}
        <div
          className={cx(
            "absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full",
            "border border-accent bg-paper shadow-[0_1px_2px_rgba(20,22,28,0.2)]",
            "opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100",
            clip.playing && "opacity-100",
          )}
          style={{ left: `${played * 100}%` }}
        />
      </div>

      <span className="nums flex-none text-xs text-ink-faint">
        {clip.loading ? "Loading…" : `${clock(clip.at)} / ${clock(clip.total)}`}
      </span>

      {clip.error && <span className="flex-none text-xs text-alert">{clip.error}</span>}
    </div>
  );
}

/**
 * A 32px square that starts one clip and stops when somebody starts another.
 *
 * `active` IS "YOU ARE THE ONE PLAYING", decided by the LIST rather than by this button, and
 * the effect below is what enforces it. The alternative - a ref to every row's audio element
 * held by the parent - is the same mechanism written twice as long. Two voices talking over
 * each other is the fastest way to make a list of forty voices, or fifty calls, useless.
 */
export function ClipSquare({
  path,
  label,
  active,
  onPlay,
  variant = "outlined",
}: {
  path: string;
  label: string;
  active: boolean;
  onPlay: () => void;
  /**
   * TWO NAMED LOOKS RATHER THAN A `className` TO OVERRIDE WITH, and the reason is a trap
   * this codebase has hit twice (see the note on `Chip` in `Calls.tsx`): a utility passed in
   * to cancel a base class wins or loses depending on which one the stylesheet happens to
   * emit last, and a class that silently loses reads as a decision somebody made.
   *
   * `outlined` is a control in a card - the voice picker's forty rows, where the button is
   * the only thing in its column. `quiet` is a glyph in a log, where fifty bordered circles
   * down the left edge would draw a column of buttons rather than a list of calls.
   */
  variant?: "outlined" | "quiet";
}) {
  const clip = useClip(path);

  // `stop` is a fresh closure each render, so it is read through a ref rather than listed as
  // a dependency - listing it would run this on every render and silence the row that is
  // playing.
  const stop = useRef(clip.stop);
  stop.current = clip.stop;
  useEffect(() => {
    if (!active) stop.current();
  }, [active]);

  return (
    <button
      type="button"
      aria-label={clip.playing ? `Stop ${label}` : label}
      title={clip.playing ? "Stop" : "Play"}
      aria-busy={clip.loading || undefined}
      disabled={clip.loading}
      onClick={() => {
        onPlay();
        clip.toggle();
      }}
      className={cx(
        "grid size-8 flex-none place-items-center rounded-full transition-colors",
        "disabled:cursor-default disabled:opacity-55",
        variant === "outlined"
          ? "border border-line bg-paper text-ink hover:border-line-strong hover:bg-line-soft"
          : "text-ink-ghost hover:bg-line-soft hover:text-ink",
      )}
    >
      {clip.playing ? <Pause size={15} /> : <Play size={15} fill="currentColor" />}
      {clip.error && <span className="sr-only">{clip.error}</span>}
    </button>
  );
}

/**
 * The shape every control in a strip takes: 32px square, one border, one hover.
 *
 * NOT `Button`. Its padding is 15px of horizontal air for a word, and a 32px control with no
 * word in it wants none - overriding that with a utility is a fight about which class the
 * stylesheet happens to emit last, which is exactly the kind of thing that ends up two
 * pixels wrong on one screen.
 */
export const CONTROL =
  "grid size-8 flex-none place-items-center border border-line bg-paper transition-colors " +
  "hover:border-line-strong hover:bg-line-soft " +
  "disabled:cursor-default disabled:opacity-55 disabled:hover:border-line disabled:hover:bg-paper";

/** `0:06`, `5:04`. Minutes, because a call that needs hours is a different problem. */
export function clock(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}
